import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

// Используем относительный URL для API, чтобы избежать проблем с SSL
const getApiUrl = () => {
  const envUrl = process.env.REACT_APP_BACKEND_URL;
  if (envUrl && (envUrl.includes('localhost') || envUrl.includes('127.0.0.1'))) {
    return envUrl;
  }
  return ''; // Относительный URL для production
};

const API_URL = getApiUrl();

const Login = ({ onLoginSuccess }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(() => sessionStorage.getItem('auth_message') || '');
  const navigate = useNavigate();
  const { login: setAuthUser } = useAuth();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const apiUrl = API_URL ? `${API_URL}/api/auth/login` : '/api/auth/login';
      console.log('Attempting login with:', { username, API_URL: apiUrl });
      
      const response = await axios.post(apiUrl, {
        username,
        password,
      }, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = response.data;
      console.log('Login successful:', { hasToken: !!data.access_token, user: data.user });
      
      // Store token and user data in localStorage
      localStorage.setItem('auth_token', data.access_token);
      localStorage.setItem('user_data', JSON.stringify(data.user));
      
      console.log('✅ Token and user data saved to localStorage');
      console.log('User:', data.user);
      
      // Update auth context IMMEDIATELY
      if (setAuthUser) {
        setAuthUser(data.user);
        console.log('✅ Auth context updated');
      }
      
      // Call success callback
      if (onLoginSuccess) {
        onLoginSuccess(data.user);
      }
      
      // Small delay to ensure everything is saved
      await new Promise(resolve => setTimeout(resolve, 200));
      
      // Verify data is saved
      const savedToken = localStorage.getItem('auth_token');
      const savedUser = localStorage.getItem('user_data');
      console.log('Verification - Token saved:', !!savedToken, 'User saved:', !!savedUser);
      
      // Navigate to dashboard
      navigate('/', { replace: true });
    } catch (err) {
      console.error('Login failed:', err.response?.status || 'network');
      console.error('Error details:', {
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        message: err.message,
        config: {
          url: err.config?.url,
          method: err.config?.method,
        }
      });
      
      // Более детальная обработка ошибок
      let errorMessage = 'Invalid credentials';
      if (err.response) {
        // Ошибка от сервера
        if (err.response.data) {
          if (err.response.data.detail) {
            errorMessage = err.response.data.detail;
          } else if (err.response.data.error) {
            errorMessage = err.response.data.error;
          } else if (err.response.data.message) {
            errorMessage = err.response.data.message;
          } else if (Array.isArray(err.response.data.details)) {
            // Ошибки валидации
            const validationErrors = err.response.data.details.map(d => d.message || d.msg).join(', ');
            errorMessage = `Validation error: ${validationErrors}`;
          }
        }
        if (err.response.status === 400) {
          errorMessage = 'Некорректный запрос. Проверьте данные.';
        } else if (err.response.status === 401) {
          errorMessage = 'Неверное имя пользователя или пароль';
        } else if (err.response.status === 500) {
          errorMessage = 'Ошибка сервера. Попробуйте позже.';
        }
      } else if (err.request) {
        // Запрос отправлен, но ответа нет
        errorMessage = 'Сервер не отвечает. Проверьте подключение.';
      } else {
        // Ошибка при настройке запроса
        errorMessage = err.message || 'Ошибка при отправке запроса';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-gray-800 rounded-lg shadow-2xl p-8 border border-gray-700">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">TON Wallet Manager</h1>
          <p className="text-gray-400">Sign in to your account</p>
        </div>
        
        <form onSubmit={handleLogin} className="space-y-6">
          {error && (
            <div className="bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 rounded">
              {error}
            </div>
          )}
          
          <div>
            <label htmlFor="username" className="block text-sm font-medium text-gray-300 mb-2">
              Username
            </label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter username"
              required
              disabled={loading}
            />
          </div>
          
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter password"
              required
              disabled={loading}
            />
          </div>
          
          <button
            type="submit"
            disabled={loading}
            className={`w-full py-3 rounded-lg font-semibold text-white transition-all ${
              loading
                ? 'bg-gray-600 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700 active:scale-95'
            }`}
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Signing in...
              </span>
            ) : (
              'Sign In'
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Login;
